import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Sparkles, Video, AlertCircle, ScanLine, Square } from 'lucide-react';
import { cn } from '@/utils/helpers';
import { videoAPI } from '@/services/api';
import { useChatStore } from '@/store';
import { savePendingTask, removePendingTask } from '@/utils/taskStorage';
import { formatSimilarUsersHotResponse } from '@/utils/taskFormatter';

interface SimilarUser {
  id: string;
  sec_uid: string;
  nickname: string;
  avatar: string;
  signature: string;
  follower_count: number;
  following_count: number;
  aweme_count: number;
  search_keywords: string;
  created_at: number;
}

// session-scoped localStorage key 辅助函数
const getSessionKey = (base: string, sessionId: string | null) =>
  sessionId ? `${base}_${sessionId}` : base;

interface ChatInputProps {
  onSend: (message: string) => void;
  isLoading?: boolean;
  placeholder?: string;
  onQuickAction?: (action: string, data: any) => void;
}

// 抖音视频和作者信息类型（基于LLM解析）
interface DouyinVideoInfo {
  recordId: string;
  url: string;
  awemeId?: string;
  // 原始文本数据
  videoRawText?: string;
  authorRawText?: string;
  authorVideosText?: string;
  // LLM分析结果
  analysis?: {
    category: string;
    contentType: string;
    targetAudience: string;
    engagementLevel: string;
    summary: string;
    strengths: string[];
    suggestions: string[];
  };
}

export function ChatInput({ onSend, isLoading, placeholder, onQuickAction: _onQuickAction }: ChatInputProps) {
  const [input, setInput] = useState('');
  const [quickActionLoading, setQuickActionLoading] = useState<string | null>(null);
  const { addMessage, updateLastMessage, setSessionVideoContext } = useChatStore();
  const currentSessionId = useChatStore((state) => state.currentSessionId);
  const stopPollingRef = useRef(false);
  const activeTaskIdRef = useRef('');
  const extractPageRef = useRef<number>(1);

  const cancelQuickAction = useCallback(async () => {
    stopPollingRef.current = true;
    const taskId = activeTaskIdRef.current;
    if (taskId) {
      try { await videoAPI.cancelTask(taskId); } catch (e) { /* ignore */ }
      removePendingTask(taskId);
      activeTaskIdRef.current = '';
    }
    updateLastMessage('❌ 已取消', undefined, 'error');
    setQuickActionLoading(null);
  }, [updateLastMessage]);
  
  const [videoInfo, setVideoInfo] = useState<DouyinVideoInfo | null>(null);
  
  const [isParsing, setIsParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [similarUsers, setSimilarUsers] = useState<SimilarUser[]>([]);
  const [, setLoadingSimilarUsers] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // 加载同类用户列表
  const loadSimilarUsers = useCallback(async (showToast = false) => {
    try {
      setLoadingSimilarUsers(true);
      console.log('[ChatInput] Loading similar users from database...');
      const response = await videoAPI.getMySimilarUsers(50);
      if (response.success && response.data) {
        const users = response.data.users || [];
        console.log(`[ChatInput] Loaded ${users.length} similar users from database`);
        setSimilarUsers(users);
        if (showToast && users.length > 0) {
          // 可以在这里显示提示
          console.log(`[ChatInput] Displaying ${users.length} similar users`);
        }
      } else {
        console.warn('[ChatInput] Failed to load similar users:', response.message);
      }
    } catch (err) {
      console.error('[ChatInput] Failed to load similar users:', err);
    } finally {
      setLoadingSimilarUsers(false);
    }
  }, []);

  const formatNumber = (num: number): string => {
    if (!num) return '0';
    if (num >= 10000) return (num / 10000).toFixed(1) + 'w';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
    return num.toString();
  };

  // 当 currentSessionId 变化时，恢复该会话的上下文数据
  useEffect(() => {
    if (!currentSessionId) {
      setVideoInfo(null);
      setSimilarUsers([]);
      extractPageRef.current = 1;
      setInput('');
      setParseError(null);
      return;
    }

    // 恢复 videoInfo（优先读 session-scoped，兼容旧版 global key）
    try {
      const sessionKey = getSessionKey('xuxuai_parsed_video', currentSessionId);
      let saved = localStorage.getItem(sessionKey);
      if (!saved) saved = localStorage.getItem('xuxuai_parsed_video'); // 兼容旧数据
      setVideoInfo(saved ? JSON.parse(saved) : null);
    } catch {
      setVideoInfo(null);
    }

    // 恢复 similarUsers（仅从 session-scoped localStorage 恢复，不再自动加载全局数据）
    try {
      const sessionKey = getSessionKey('xuxuai_similar_users', currentSessionId);
      const saved = localStorage.getItem(sessionKey);
      setSimilarUsers(saved ? JSON.parse(saved) : []);
    } catch {
      setSimilarUsers([]);
    }

    // 恢复 extractPage
    try {
      const sessionKey = getSessionKey('oc_extract_page', currentSessionId);
      let saved = localStorage.getItem(sessionKey);
      if (!saved) saved = localStorage.getItem('oc_extract_page'); // 兼容旧数据
      extractPageRef.current = saved ? parseInt(saved, 10) : 1;
    } catch {
      extractPageRef.current = 1;
    }

    // 清空会话级别的临时状态
    setInput('');
    setParseError(null);
  }, [currentSessionId]);

  // 保存 videoInfo 到 session-scoped localStorage
  useEffect(() => {
    if (!currentSessionId) return;
    const key = getSessionKey('xuxuai_parsed_video', currentSessionId);
    if (videoInfo) {
      localStorage.setItem(key, JSON.stringify(videoInfo));
    } else {
      localStorage.removeItem(key);
    }
  }, [videoInfo, currentSessionId]);

  // 保存 similarUsers 到 session-scoped localStorage
  useEffect(() => {
    if (!currentSessionId) return;
    const key = getSessionKey('xuxuai_similar_users', currentSessionId);
    if (similarUsers.length > 0) {
      localStorage.setItem(key, JSON.stringify(similarUsers));
    } else {
      localStorage.removeItem(key);
    }
  }, [similarUsers, currentSessionId]);

  // 处理单条评论分析请求
  const handleAnalyzeCommentSend = useCallback(async (message: string) => {
    stopPollingRef.current = false;
    activeTaskIdRef.current = '';
    const taskSessionId = currentSessionId; // 锁定当前会话

    // 从消息中提取评论ID或链接
    const urlMatch = message.match(/(https?:\/\/)?(v\.douyin\.com|www\.douyin\.com)\/[^\s]+/);
    const idMatch = message.match(/评论ID[:：]?\s*(\d+)/) || message.match(/\b(\d{10,})\b/);
    
    const url = urlMatch ? urlMatch[0] : undefined;
    let commentId = idMatch ? idMatch[1] : '';
    let awemeId = '';

    // 如果提供了URL但没有独立的commentId，尝试从URL中解析
    if (url && !commentId) {
      // 抖音分享链接通常包含 comment_id 参数
      const commentIdMatch = url.match(/[?&]comment_id=(\d+)/);
      if (commentIdMatch) {
        commentId = commentIdMatch[1];
      }
    }

    // 如果没有提供视频链接，但当前已解析了视频，则自动使用该视频
    let finalUrl = url;
    if (!finalUrl && videoInfo?.url) {
      finalUrl = videoInfo.url;
    }

    if (!commentId) {
      setParseError('未能识别评论ID，请在输入框末尾补充评论ID后重试');
      return;
    }
    if (!finalUrl) {
      setParseError('缺少视频链接，请先解析一个视频，或在输入框中补充视频链接');
      return;
    }

    // 添加用户消息到聊天
    addMessage({
      role: 'user',
      content: message,
      type: 'text',
    }, taskSessionId);
    setInput('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    // 添加助手loading消息
    addMessage({
      role: 'assistant',
      content: '⏳ 正在分析评论内容，请稍候...',
      type: 'analysis',
      data: { action: '评论分析', status: 'pending' }
    }, taskSessionId);

    setQuickActionLoading('评论分析');

    let taskId = '';
    try {
      const createResponse = await videoAPI.analyzeComment(commentId, awemeId || undefined, finalUrl);
      if (!createResponse.success || !createResponse.data?.taskId) {
        throw new Error('创建任务失败');
      }
      
      taskId = createResponse.data.taskId;
      activeTaskIdRef.current = taskId;
      savePendingTask(taskId, '评论分析');
      
      const pollTask = async (): Promise<any> => {
        const maxAttempts = 3600;
        const interval = 1000;
        for (let i = 0; i < maxAttempts; i++) {
          if (stopPollingRef.current) throw new Error('已取消');
          await new Promise(resolve => setTimeout(resolve, interval));
          if (stopPollingRef.current) throw new Error('已取消');
          const statusResponse = await videoAPI.getTaskStatus(taskId);
          if (!statusResponse.success) continue;
          const task = statusResponse.data;
          if (!task) continue;
          if (task.status === 'completed') {
            return task.result;
          } else if (task.status === 'failed') {
            throw new Error(task.error || '任务执行失败');
          }
          if (task.message) {
            updateLastMessage(
              `⏳ ${task.message}${task.progress !== undefined ? ` (${task.progress}%)` : ''}`,
              { action: '评论分析', taskId, status: task.status, progress: task.progress },
              'analysis',
              taskSessionId
            );
          }
        }
        throw new Error('轮询超时，请稍后通过"刷新"按钮查看结果');
      };
      
      const result = await pollTask();
      const comment = result?.comment || {};
      const replies = result?.replies || [];
      const analysis = result?.analysis || {};
      const reply = result?.reply || {};
      
      let content = `### 📊 评论分析结果\n\n`;
      content += `**目标评论**\n`;
      content += `\n> 💬 ${comment.content || '无内容'}\n\n`;
      content += `👍 ${comment.digg_count || 0}  ·  💬 ${replies.length} 条回复\n\n`;
      
      content += `**分析结论**\n`;
      content += `• 情感倾向：${analysis.sentiment === 'positive' ? '正面 😊' : analysis.sentiment === 'negative' ? '负面 😤' : '中性 😐'}\n`;
      content += `• 用户意图：${analysis.intent || '未知'}\n`;
      if (analysis.key_points?.length) {
        content += `• 关键要点：${analysis.key_points.join('、')}\n`;
      }
      if (analysis.user_profile) {
        content += `• 用户画像：${analysis.user_profile}\n`;
      }
      content += '\n';
      
      if (replies.length > 0) {
        content += `**回复汇总**（共 ${replies.length} 条）\n`;
        content += replies.slice(0, 5).map((r: any, idx: number) => 
          `${idx + 1}. ${r.content?.substring(0, 50) || ''}${r.content?.length > 50 ? '...' : ''}`
        ).join('\n');
        if (replies.length > 5) content += `\n...还有 ${replies.length - 5} 条回复`;
        content += '\n\n';
      }
      
      content += `**AI 回复建议**\n`;
      content += `> ${reply.content || '暂无建议'}\n\n`;
      content += `💡 策略：${reply.strategy || '直接回复'}  ·  语气：${reply.tone || '友好'}`;
      
      removePendingTask(taskId);
      updateLastMessage(
        content,
        { action: '评论分析', comment, replies, analysis, reply, taskId, status: 'completed' },
        'analysis',
        taskSessionId
      );
    } catch (err: any) {
      if (taskId) removePendingTask(taskId);
      const errMsg = typeof err === 'string' ? err : (err?.message || '未知错误');
      if (errMsg === '已取消') {
        updateLastMessage('❌ 已取消', undefined, 'error', taskSessionId);
      } else {
        updateLastMessage(
          `❌ 评论分析失败：${errMsg}`,
          undefined,
          'error',
          taskSessionId
        );
      }
    } finally {
      activeTaskIdRef.current = '';
      setQuickActionLoading(null);
    }
  }, [addMessage, updateLastMessage, videoInfo, currentSessionId]);

  const handleFindSimilarUsersSend = useCallback(async (message: string) => {
    stopPollingRef.current = false;
    activeTaskIdRef.current = '';
    const taskSessionId = currentSessionId; // 锁定当前会话
    const requirementsMatch = message.match(/搜索要求[：:]?\s*(.*)/);
    const requirements = requirementsMatch ? requirementsMatch[1].trim() : undefined;
    if (!videoInfo) {
      setParseError('请先解析一个视频获取账号profile');
      return;
    }
    const profileData = {
      url: videoInfo.url,
      analysis: videoInfo.analysis,
      videoRawText: videoInfo.videoRawText?.substring(0, 2000),
      authorRawText: videoInfo.authorRawText?.substring(0, 2000),
    };
    addMessage({ role: 'user', content: message, type: 'text' }, taskSessionId);
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    setQuickActionLoading('获取同类');
    let taskId = '';
    try {
      const createResponse = await videoAPI.findSimilarUsers(videoInfo.recordId, profileData, requirements);
      if (!createResponse.success || !createResponse.data?.taskId) throw new Error('创建任务失败');
      taskId = createResponse.data.taskId;
      activeTaskIdRef.current = taskId;
      savePendingTask(taskId, '获取同类');
      addMessage({
        role: 'assistant',
        content: `⏳ 正在获取同类账号，请稍候...（任务ID: ${taskId.slice(0, 8)}）`,
        type: 'analysis',
        data: { action: '获取同类', taskId, status: 'pending' }
      }, taskSessionId);
      const pollTask = async (): Promise<any> => {
        const maxAttempts = 3600;
        const interval = 1000;
        for (let i = 0; i < maxAttempts; i++) {
          if (stopPollingRef.current) throw new Error('已取消');
          await new Promise(resolve => setTimeout(resolve, interval));
          if (stopPollingRef.current) throw new Error('已取消');
          const statusResponse = await videoAPI.getTaskStatus(taskId);
          if (!statusResponse.success) continue;
          const task = statusResponse.data;
          if (!task) continue;
          if (task.status === 'completed') return task.result;
          else if (task.status === 'failed') throw new Error(task.error || '任务执行失败');
          if (task.message) {
            updateLastMessage(
              `⏳ ${task.message}${task.progress !== undefined ? ` (${task.progress}%)` : ''}`,
              { action: '获取同类', taskId, status: task.status, progress: task.progress },
              'analysis',
              taskSessionId
            );
          }
        }
        throw new Error('轮询超时，请稍后通过"刷新"按钮查看结果');
      };
      const result = await pollTask();
      const users = result?.users || [];
      const keywords = result?.keywords || [];
      if (users.length > 0) {
        setSimilarUsers(users);
        setTimeout(() => {
          // 确保仍在原会话中才刷新，避免数据写入错误的会话
          if (useChatStore.getState().currentSessionId === taskSessionId) {
            loadSimilarUsers(true);
          }
        }, 500);
      }
      const content = `✅ 找到 ${users.length} 个同类账号\n\n` +
        `${keywords.length ? `🔍 搜索关键词：${keywords.join('、')}\n\n` : ''}` +
        users.map((u: any, i: number) => `${i + 1}. ${u.nickname}（粉丝${formatNumber(u.follower_count || 0)}）`).join('\n');
      removePendingTask(taskId);
      updateLastMessage(
        content,
        { action: '获取同类', users: users.slice(0, 10), keywords, taskId, totalCount: users.length, status: 'completed' },
        'analysis',
        taskSessionId
      );
    } catch (err: any) {
      if (taskId) removePendingTask(taskId);
      const errMsg = typeof err === 'string' ? err : (err?.message || '获取同类失败');
      if (errMsg === '已取消') updateLastMessage('❌ 已取消', undefined, 'error', taskSessionId);
      else updateLastMessage(`❌ 获取同类失败：${errMsg}`, undefined, 'error', taskSessionId);
    } finally {
      activeTaskIdRef.current = '';
      setQuickActionLoading(null);
    }
  }, [addMessage, updateLastMessage, videoInfo, loadSimilarUsers, currentSessionId]);

  const handleSimilarUsersHotSend = useCallback(async (message: string) => {
    stopPollingRef.current = false;
    activeTaskIdRef.current = '';
    const taskSessionId = currentSessionId; // 锁定当前会话
    const countMatch = message.match(/每个账号视频数[：:]?\s*(\d+)/);
    const count = countMatch ? parseInt(countMatch[1], 10) : 5;
    addMessage({ role: 'user', content: message, type: 'text' }, taskSessionId);
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    setQuickActionLoading('同类热门');
    let taskId = '';
    try {
      const createResponse = await videoAPI.getSimilarUsersHot(undefined, count);
      if (!createResponse.success || !createResponse.data?.taskId) throw new Error('创建任务失败');
      taskId = createResponse.data.taskId;
      activeTaskIdRef.current = taskId;
      savePendingTask(taskId, '同类热门');
      addMessage({
        role: 'assistant',
        content: `⏳ 正在获取同类热门视频，请稍候...（任务ID: ${taskId.slice(0, 8)}）`,
        type: 'analysis',
        data: { action: '同类热门', taskId, status: 'pending' }
      }, taskSessionId);
      const pollTask = async (): Promise<any> => {
        const maxAttempts = 3600;
        const interval = 1000;
        for (let i = 0; i < maxAttempts; i++) {
          if (stopPollingRef.current) throw new Error('已取消');
          await new Promise(resolve => setTimeout(resolve, interval));
          if (stopPollingRef.current) throw new Error('已取消');
          const statusResponse = await videoAPI.getTaskStatus(taskId);
          if (!statusResponse.success) continue;
          const task = statusResponse.data;
          if (!task) continue;
          if (task.status === 'completed') return task.result;
          else if (task.status === 'failed') throw new Error(task.error || '任务执行失败');
          const currentHotVideos = task.result?.hotVideos || [];
          if (currentHotVideos.length > 0) {
            const partialContent = formatSimilarUsersHotResponse(task.result);
            updateLastMessage(
              `⏳ ${task.message}${task.progress !== undefined ? ` (${task.progress}%)` : ''}\n\n${partialContent}`,
              { action: '同类热门', taskId, status: task.status, progress: task.progress, hotVideos: currentHotVideos, userCount: task.result?.users?.length },
              'analysis',
              taskSessionId
            );
          } else if (task.message) {
            updateLastMessage(
              `⏳ ${task.message}${task.progress !== undefined ? ` (${task.progress}%)` : ''}`,
              { action: '同类热门', taskId, status: task.status, progress: task.progress },
              'analysis',
              taskSessionId
            );
          }
        }
        throw new Error('轮询超时，请稍后通过"刷新"按钮查看结果');
      };
      const result = await pollTask();
      const hotVideos = result?.hotVideos || [];
      const users = result?.users || [];
      const totalVideos = hotVideos.reduce((sum: number, item: any) => sum + (item.videos?.length || 0), 0);
      const contentParts: string[] = [];
      contentParts.push(`✅ 从 ${users.length} 个同类账号中获取到 ${totalVideos} 个热门视频\n`);
      hotVideos.forEach((item: any) => {
        const user = item.user;
        const videos = item.videos || [];
        if (videos.length === 0) return;
        contentParts.push(`\n👤 ${user.nickname}（粉丝 ${formatNumber(user.follower_count || 0)}）`);
        contentParts.push('─'.repeat(40));
        videos.forEach((video: any, videoIndex: number) => {
          const title = video.title || video.desc || '无标题';
          const playCount = formatNumber(video.play_count || 0);
          const diggCount = formatNumber(video.digg_count || 0);
          const commentCount = formatNumber(video.comment_count || 0);
          const shareUrl = video.share_url || `https://www.douyin.com/video/${video.aweme_id}`;
          contentParts.push(
            `${videoIndex + 1}. ${title.substring(0, 60)}${title.length > 60 ? '...' : ''}\n` +
            `   ▶️ ${playCount}  👍 ${diggCount}  💬 ${commentCount}\n` +
            `   🔗 [打开视频](${shareUrl})`
          );
        });
      });
      removePendingTask(taskId);
      updateLastMessage(
        contentParts.join('\n'),
        { action: '同类热门', hotVideos, userCount: users.length, totalVideos, taskId, status: 'completed' },
        'analysis',
        taskSessionId
      );
    } catch (err: any) {
      if (taskId) removePendingTask(taskId);
      const errMsg = typeof err === 'string' ? err : (err?.message || '同类热门失败');
      if (errMsg === '已取消') updateLastMessage('❌ 已取消', undefined, 'error', taskSessionId);
      else updateLastMessage(`❌ 同类热门失败：${errMsg}`, undefined, 'error', taskSessionId);
    } finally {
      activeTaskIdRef.current = '';
      setQuickActionLoading(null);
    }
  }, [addMessage, updateLastMessage, currentSessionId]);

  const handleExtractLeadsSend = useCallback(async (message: string) => {
    stopPollingRef.current = false;
    activeTaskIdRef.current = '';
    const taskSessionId = currentSessionId; // 锁定当前会话
    const criteriaMatch = message.match(/筛选标准[：:]?\s*(.+)/);
    const criteria = criteriaMatch ? criteriaMatch[1].trim() : undefined;
    const batchSizeMatch = message.match(/每批视频数[：:]?\s*(\d+)/);
    const batchSize = batchSizeMatch ? parseInt(batchSizeMatch[1], 10) : 10;
    const countMatch = message.match(/每视频评论数[：:]?\s*(\d+)/);
    const count = countMatch ? parseInt(countMatch[1], 10) : 100;
    addMessage({ role: 'user', content: message, type: 'text' }, taskSessionId);
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    setQuickActionLoading('评论提炼');
    try {
      const hotRes = await videoAPI.getSavedHotVideos(50);
      if (!hotRes.success || !hotRes.data?.videos?.length) {
        setParseError('暂无已保存的视频，请先使用"同类热门"获取视频');
        setQuickActionLoading(null);
        return;
      }
      const allVideos = hotRes.data.videos;
      const totalPages = Math.max(1, Math.ceil(allVideos.length / batchSize));
      let currentPage = extractPageRef.current;
      if (currentPage > totalPages) { currentPage = 1; extractPageRef.current = 1; }
      const start = (currentPage - 1) * batchSize;
      const videos = allVideos.slice(start, start + batchSize);
      addMessage({
        role: 'assistant',
        content: `📄 第 ${currentPage}/${totalPages} 批评论提炼，本批共 ${videos.length} 个视频`,
        type: 'analysis',
      }, taskSessionId);
      const pollTask = async (taskId: string, videoTitle: string): Promise<any> => {
        const maxAttempts = 3600;
        const interval = 1000;
        for (let i = 0; i < maxAttempts; i++) {
          if (stopPollingRef.current) throw new Error('已取消');
          await new Promise(resolve => setTimeout(resolve, interval));
          if (stopPollingRef.current) throw new Error('已取消');
          const statusResponse = await videoAPI.getTaskStatus(taskId);
          if (!statusResponse.success) continue;
          const task = statusResponse.data;
          if (!task) continue;
          if (task.status === 'completed') return task.result;
          else if (task.status === 'failed') throw new Error(task.error || '任务执行失败');
          if (task.message) {
            updateLastMessage(
              `⏳ 正在提炼视频：${videoTitle}\n${task.message}${task.progress !== undefined ? ` (${task.progress}%)` : ''}`,
              { action: '评论提炼', taskId, status: task.status, progress: task.progress },
              'analysis',
              taskSessionId
            );
          }
        }
        throw new Error('轮询超时，请稍后通过"刷新"按钮查看结果');
      };
      for (let i = 0; i < videos.length; i++) {
        if (stopPollingRef.current) break;
        const v = videos[i];
        const url = v.share_url || (v.aweme_id ? `https://www.douyin.com/video/${v.aweme_id}` : '');
        const awemeId = v.aweme_id;
        if (!url && !awemeId) continue;
        addMessage({
          role: 'assistant',
          content: `⏳ 第 ${currentPage}/${totalPages} 批：正在提炼第 ${i + 1}/${videos.length} 个视频：${v.title || v.desc || '无标题'}`,
          type: 'loading',
        }, taskSessionId);
        let videoTaskId = '';
        try {
          const createResponse = await videoAPI.extractLeads(url, awemeId, count, currentPage, criteria);
          if (!createResponse.success || !createResponse.data?.taskId) {
            updateLastMessage(`❌ 第 ${currentPage}/${totalPages} 批：第 ${i + 1} 个视频提炼失败：创建任务失败`, undefined, 'error', taskSessionId);
            continue;
          }
          videoTaskId = createResponse.data.taskId;
          activeTaskIdRef.current = videoTaskId;
          savePendingTask(videoTaskId, '评论提炼');
          const result = await pollTask(videoTaskId, v.title || v.desc || '无标题');
          const leads = result?.leads || [];
          const allComments = result?.allComments || [];
          const displayComments = result?.displayComments || allComments;
          const leadCount = allComments.filter((c: any) => c.is_lead).length;
          if (leads.length > 0) setExtractedLeads((prev) => [...(prev || []), ...leads]);
          let content = `✅ 第 ${currentPage}/${totalPages} 批：第 ${i + 1}/${videos.length} 个视频提炼完成\n\n` +
            `**${v.title || v.desc || '无标题'}**\n` +
            `共 ${allComments.length} 条评论，${leadCount} 条潜在客户\n\n`;
          if (displayComments.length > 0) {
            content += displayComments.map((c: any, idx: number) => {
              const userName = c.nickname || c.username || '匿名';
              const profileLink = c.profile_url || (c.sec_uid ? `https://www.douyin.com/user/${c.sec_uid}` : '');
              const userPart = profileLink ? `[${userName}](${profileLink})` : userName;
              const cid = c.comment_id || 'N/A';
              const leadMark = c.is_lead ? ` **[${c.type || '潜在客户'}]**` : '';
              return `${idx + 1}. ${c.is_lead ? '🔴' : '⚪'} @${userPart} \`ID:${cid}\`${leadMark}\n> ${c.content?.substring(0, 80) || ''}${c.content?.length > 80 ? '...' : ''}`;
            }).join('\n\n');
            if (displayComments.length > 10) content += `\n\n...还有 ${displayComments.length - 10} 条评论`;
          } else {
            content += '暂无评论';
          }
          removePendingTask(videoTaskId);
          updateLastMessage(
            content,
            { action: '评论提炼', allComments: displayComments.slice(0, 20), leads: leads.slice(0, 10), totalCount: leads.length, videoTitle: v.title || v.desc, taskId: videoTaskId, status: 'completed' },
            'analysis',
            taskSessionId
          );
        } catch (err: any) {
          if (videoTaskId) removePendingTask(videoTaskId);
          if (err?.message === '已取消') { updateLastMessage('❌ 已取消', undefined, 'error', taskSessionId); break; }
          updateLastMessage(`❌ 第 ${currentPage}/${totalPages} 批：第 ${i + 1} 个视频提炼失败：${err?.message || '未知错误'}`, undefined, 'error', taskSessionId);
        }
      }
      const nextPage = currentPage >= totalPages ? 1 : currentPage + 1;
      extractPageRef.current = nextPage;
      try { localStorage.setItem(getSessionKey('oc_extract_page', currentSessionId), String(nextPage)); } catch {}
    } catch (err: any) {
      const errMsg = typeof err === 'string' ? err : (err?.message || '评论提炼失败');
      if (errMsg !== '已取消') setParseError(errMsg);
    } finally {
      activeTaskIdRef.current = '';
      setQuickActionLoading(null);
    }
  }, [addMessage, updateLastMessage, currentSessionId]);

  const handleSend = useCallback(() => {
    if (!input.trim() || isLoading) return;
    const trimmed = input.trim();
    if (trimmed.startsWith('【评论分析】')) {
      handleAnalyzeCommentSend(trimmed);
      return;
    }
    if (trimmed.startsWith('【获取同类】')) {
      handleFindSimilarUsersSend(trimmed);
      return;
    }
    if (trimmed.startsWith('【同类热门】')) {
      handleSimilarUsersHotSend(trimmed);
      return;
    }
    if (trimmed.startsWith('【评论提炼】')) {
      handleExtractLeadsSend(trimmed);
      return;
    }
    onSend(trimmed);
    setInput('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [input, isLoading, onSend, handleAnalyzeCommentSend, handleFindSimilarUsersSend, handleSimilarUsersHotSend, handleExtractLeadsSend]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setInput(value);
    setParseError(null);
    
    // 自动调整高度
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
  };

  // 解析视频链接
  const handleParseVideo = async () => {
    const douyinMatch = input.match(/(https?:\/\/)?(v\.douyin\.com|www\.douyin\.com)\/[^\s]+/);
    if (!douyinMatch) {
      setParseError('请输入有效的抖音链接');
      return;
    }

    // 清理 URL 末尾的中文标点、括号等干扰字符
    const url = douyinMatch[0].replace(/[【】\[\]\uff08\uff09\u3002\uff0c\uff1f\uff01]+$/, '');
    setIsParsing(true);
    setParseError(null);
    stopPollingRef.current = false;
    activeTaskIdRef.current = '';
    const taskSessionId = currentSessionId; // 锁定当前会话

    try {
      console.log('[ChatInput] Parsing video URL:', url);
      const response = await videoAPI.parseProfile(url);

      if (!response.success || !response.data?.taskId) {
        throw new Error('创建任务失败');
      }

      const taskId = response.data.taskId;
      activeTaskIdRef.current = taskId;
      savePendingTask(taskId, '解析视频');

      // 清空输入框
      setInput('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.value = '';
      }

      // 添加解析请求消息到聊天
      addMessage({
        role: 'user',
        content: `【解析视频】${url}`,
        type: 'text',
      }, taskSessionId);

      addMessage({
        role: 'assistant',
        content: `⏳ 正在解析视频信息，请稍候...（任务ID: ${taskId.slice(0, 8)}）`,
        type: 'analysis',
        data: { action: '解析视频', taskId, status: 'pending' },
      }, taskSessionId);

      // 轮询查询任务状态
      const pollTask = async (): Promise<any> => {
        const maxAttempts = 3600;
        const interval = 1000;
        for (let i = 0; i < maxAttempts; i++) {
          if (stopPollingRef.current) throw new Error('已取消');
          await new Promise((resolve) => setTimeout(resolve, interval));
          if (stopPollingRef.current) throw new Error('已取消');
          const statusResponse = await videoAPI.getTaskStatus(taskId);
          if (!statusResponse.success) continue;
          const task = statusResponse.data;
          if (!task) continue;
          if (task.status === 'completed') {
            return task.result;
          } else if (task.status === 'failed') {
            throw new Error(task.error || '任务执行失败');
          }
          if (task.message) {
            updateLastMessage(
              `⏳ ${task.message}${task.progress !== undefined ? ` (${task.progress}%)` : ''}`,
              { action: '解析视频', taskId, status: task.status, progress: task.progress },
              'analysis',
              taskSessionId
            );
          }
        }
        throw new Error('轮询超时，请稍后重试');
      };

      const result = await pollTask();
      const { recordId, videoRawText, authorRawText, authorVideosText, analysis } = result || {};

      setVideoInfo({
        recordId: recordId || '',
        url,
        videoRawText: videoRawText || '',
        authorRawText: authorRawText || '',
        authorVideosText: authorVideosText || '',
        analysis: analysis || null,
      });

      // 将视频主旨保存到当前会话，用于自动命名
      if (taskSessionId && analysis) {
        setSessionVideoContext(taskSessionId, {
          category: analysis.category,
          contentType: analysis.contentType,
          summary: analysis.summary,
        });
      }

      console.log('[ChatInput] Video parsed successfully, recordId:', recordId);

      const analysisSummary = analysis
        ? `视频分析完成！\n\n` +
          `${analysis.category ? `📂 分类：${analysis.category}\n` : ''}` +
          `${analysis.contentType ? `🎬 内容类型：${analysis.contentType}\n` : ''}` +
          `${analysis.engagementLevel ? `🔥 互动等级：${analysis.engagementLevel}\n` : ''}` +
          `${analysis.summary ? `📝 摘要：${analysis.summary}\n` : ''}` +
          `${analysis.strengths?.length ? `✨ 亮点：${analysis.strengths.join('、')}` : ''}`
        : '视频解析完成！';

      removePendingTask(taskId);
      updateLastMessage(
        analysisSummary,
        { action: '解析视频', recordId, analysis, taskId, status: 'completed' },
        'analysis',
        taskSessionId
      );
    } catch (error: any) {
      console.error('[ChatInput] Parse video error:', error);
      const errMsg = typeof error === 'string' ? error : (error?.message || '解析失败，请稍后重试');
      if (errMsg === '已取消') {
        updateLastMessage('❌ 已取消', undefined, 'error', taskSessionId);
      } else {
        setParseError(errMsg);
      }
    } finally {
      activeTaskIdRef.current = '';
      setIsParsing(false);
    }
  };

  // 存储提炼到的潜在客户（用于评论分析）
  const [, setExtractedLeads] = useState<any[] | null>(null);

  // 处理快捷操作
  const navigate = useNavigate();

  const handleTriggerFindSimilar = useCallback(() => {
    navigate('/content?tab=similar');
  }, [navigate]);

  const handleTriggerSimilarHot = useCallback(() => {
    navigate('/content?tab=hot');
  }, [navigate]);

  const handleQuickAction = async (action: string) => {
    // 所有快捷操作都只是填充输入框，正在执行时不可切换
    if (quickActionLoading) return;
    stopPollingRef.current = false;
    activeTaskIdRef.current = '';

    if (action === '评论分析') {
      setInput('【评论分析】\n请帮我分析以下评论并生成回复建议：\n\n评论ID/链接：');
      setTimeout(() => {
        textareaRef.current?.focus();
        const len = textareaRef.current?.value.length || 0;
        textareaRef.current?.setSelectionRange(len, len);
      }, 0);
      return;
    }

    if (action === '获取同类') {
      if (!videoInfo) {
        setParseError('请先解析一个视频获取账号profile');
        return;
      }
      const analysis = videoInfo.analysis;
      let selfDesc = '';
      if (analysis) {
        const parts: string[] = [];
        if (analysis.category && analysis.category !== '未识别') parts.push(`分类：${analysis.category}`);
        if (analysis.contentType && analysis.contentType !== '未识别') parts.push(`内容类型：${analysis.contentType}`);
        if (analysis.targetAudience && analysis.targetAudience !== '未识别') parts.push(`目标受众：${analysis.targetAudience}`);
        if (analysis.engagementLevel && analysis.engagementLevel !== '未识别') parts.push(`互动等级：${analysis.engagementLevel}`);
        if (analysis.summary) parts.push(`摘要：${analysis.summary}`);
        if (analysis.strengths?.length) parts.push(`亮点：${analysis.strengths.join('、')}`);
        selfDesc = parts.join('\n');
      }
      if (!selfDesc) {
        const raw = (videoInfo.videoRawText || '').substring(0, 300);
        selfDesc = raw ? `视频文案：\n${raw}` : '（暂无详细描述）';
      }
      setInput(`【获取同类】\n基于当前解析的账号信息，搜索同类抖音账号。\n\n当前账号描述：\n${selfDesc}\n\n搜索要求：`);
      setTimeout(() => {
        textareaRef.current?.focus();
        const len = textareaRef.current?.value.length || 0;
        textareaRef.current?.setSelectionRange(len, len);
      }, 0);
      return;
    }

    if (action === '同类热门') {
      setInput('【同类热门】\n查看已保存同类账号的热门视频。\n\n每个账号视频数：5');
      setTimeout(() => {
        textareaRef.current?.focus();
        const len = textareaRef.current?.value.length || 0;
        textareaRef.current?.setSelectionRange(len, len);
      }, 0);
      return;
    }

    if (action === '评论提炼') {
      setInput('【评论提炼】\n从已保存的热门视频中筛选潜在客户评论。\n\n筛选标准：吐槽类、咨询类、求推荐类、对比类\n每批视频数：10\n每视频评论数：100');
      setTimeout(() => {
        textareaRef.current?.focus();
        const len = textareaRef.current?.value.length || 0;
        textareaRef.current?.setSelectionRange(len, len);
      }, 0);
      return;
    }
  };

  // 快捷指令配置
  const quickActions = [
    { 
      label: '获取同类', 
      icon: Sparkles,
      disabled: !videoInfo,
      tooltip: videoInfo ? '根据当前profile搜索同类账号' : '请先解析视频获取profile',
    },
    { 
      label: '同类热门', 
      icon: Sparkles,
      disabled: similarUsers.length === 0,
      tooltip: similarUsers.length > 0 
        ? '查看已保存同类账号的热门视频' 
        : '请先使用"获取同类"功能搜索同类账号',
    },
    { 
      label: '评论提炼', 
      icon: Sparkles,
      disabled: false,
      tooltip: '根据库中已保存的视频逐条提炼评论',
    },
    { 
      label: '评论分析', 
      icon: Sparkles,
      disabled: false,
      tooltip: '分析单条评论及其回复，生成AI回复建议',
    },
  ].filter(action => action.label);

  return (
    <div className="border-t border-gray-200 bg-white p-4">
      {/* 抖音视频信息展示区域 */}
      <div className="mb-3">
        {videoInfo ? (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Video className="w-5 h-5 text-blue-500 flex-shrink-0" />
                  <span className="text-sm font-medium text-blue-900">视频解析完成</span>
                </div>
                
                {/* LLM分析结果 */}
                {videoInfo.analysis && (
                  <div className="mt-2 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {videoInfo.analysis.category && videoInfo.analysis.category !== '未识别' && (
                        <span className="bg-blue-100 px-1.5 py-0.5 rounded text-xs text-blue-700">
                          {videoInfo.analysis.category}
                        </span>
                      )}
                      {videoInfo.analysis.contentType && videoInfo.analysis.contentType !== '未识别' && (
                        <span className="bg-green-100 px-1.5 py-0.5 rounded text-xs text-green-700">
                          {videoInfo.analysis.contentType}
                        </span>
                      )}
                      {videoInfo.analysis.engagementLevel && videoInfo.analysis.engagementLevel !== '未识别' && (
                        <span className={cn(
                          "px-1.5 py-0.5 rounded text-xs",
                          videoInfo.analysis.engagementLevel.includes('爆款') && 'bg-red-100 text-red-700 font-medium',
                          videoInfo.analysis.engagementLevel.includes('热门') && 'bg-orange-100 text-orange-700',
                          videoInfo.analysis.engagementLevel.includes('良好') && 'bg-green-100 text-green-700',
                          videoInfo.analysis.engagementLevel.includes('一般') && 'bg-yellow-100 text-yellow-700',
                          videoInfo.analysis.engagementLevel.includes('冷门') && 'bg-gray-100 text-gray-700',
                        )}>
                          {videoInfo.analysis.engagementLevel}
                        </span>
                      )}
                    </div>
                    
                    {videoInfo.analysis.summary && (
                      <div className="text-xs text-blue-700 mt-1 line-clamp-2">
                        {videoInfo.analysis.summary}
                      </div>
                    )}
                    
                    {videoInfo.analysis.strengths && videoInfo.analysis.strengths.length > 0 && (
                      <div className="text-xs text-blue-600 mt-1">
                        <span className="font-medium">亮点：</span>
                        {videoInfo.analysis.strengths.join('、')}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* 快捷链接按钮行 */}
              <div className="mt-2 flex items-center gap-2 text-xs">
                <span
                  onClick={handleTriggerFindSimilar}
                  className="cursor-pointer text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                >
                  同类账号{similarUsers.length > 0 ? ` (${similarUsers.length})` : ''}
                </span>
                <span className="text-gray-300">|</span>
                <span
                  onClick={handleTriggerSimilarHot}
                  className="cursor-pointer text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                >
                  同类热门视频
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-amber-800">
                  <span className="font-medium">提示：</span>
                  在输入框中粘贴您的
                  <span className="font-medium text-amber-900"> 抖音视频链接 </span>
                  ，然后点击"解析个人视频"按钮，系统将自动提炼您的账号信息
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 解析按钮和错误提示 */}
      {!videoInfo && (
        <div className="mb-3">
          {parseError && (
            <div className="mb-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {parseError}
            </div>
          )}
          <button
            onClick={() => isParsing ? cancelQuickAction() : handleParseVideo()}
            disabled={!isParsing && !input.match(/(https?:\/\/)?(v\.douyin\.com|www\.douyin\.com)\/[^\s]+/)}
            className={cn(
              'w-full flex items-center justify-center gap-2 py-2.5 rounded-lg font-medium transition-all',
              isParsing
                ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 cursor-pointer'
                : input.match(/(https?:\/\/)?(v\.douyin\.com|www\.douyin\.com)\/[^\s]+/)
                  ? 'bg-gradient-to-r from-pink-500 to-red-500 text-white hover:from-pink-600 hover:to-red-600 shadow-md'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            )}
          >
            {isParsing ? (
              <>
                <Square className="w-4 h-4 fill-current" />
                停止解析
              </>
            ) : (
              <>
                <ScanLine className="w-4 h-4" />
                解析个人视频
              </>
            )}
          </button>
        </div>
      )}

      {/* 快捷指令 */}
      <div className="flex gap-2 mb-3 overflow-x-auto pb-2">
        {quickActions.map((action) => {
          const isLoadingThis = quickActionLoading === action.label;
          return (
            <button
              key={action.label}
              onClick={() => isLoadingThis ? cancelQuickAction() : handleQuickAction(action.label)}
              disabled={action.disabled || (!isLoadingThis && !!quickActionLoading)}
              title={action.tooltip}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors",
                action.disabled
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : isLoadingThis
                  ? "bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 cursor-pointer"
                  : quickActionLoading
                  ? "bg-gray-50 text-gray-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 border border-blue-200 text-blue-700 cursor-pointer"
              )}
            >
              {isLoadingThis ? (
                <Square size={12} className="fill-current" />
              ) : (
                <action.icon size={12} className={action.disabled ? "text-gray-400" : "text-blue-500"} />
              )}
              {action.label}
            </button>
          );
        })}
      </div>

      {/* 输入框 */}
      <div className="flex gap-2">
        {/* <button
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          title="语音输入"
        >
          <Mic size={20} />
        </button> */}
        
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder || '输入消息...\n提示：输入抖音链接可自动提炼视频信息'}
            disabled={isLoading}
            rows={1}
            className={cn(
              'w-full px-4 py-3 pr-12 bg-gray-100 rounded-xl resize-none outline-none',
              'focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'text-sm leading-relaxed'
            )}
            style={{ minHeight: '48px', maxHeight: '200px' }}
          />
          
          {/* 字数限制提示 */}
          <div className="absolute right-3 bottom-3 text-xs text-gray-400 pointer-events-none">
            {input.length > 0 && `${input.length}`}
          </div>
        </div>

        {/* 发送按钮 */}
        <button
          onClick={handleSend}
          disabled={!input.trim() || isLoading}
          className={cn(
            'p-3 rounded-xl transition-all',
            input.trim() && !isLoading
              ? 'bg-primary-500 text-white hover:bg-primary-600 shadow-md hover:shadow-lg'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          )}
        >
          <Send size={20} />
        </button>
      </div>

      {/* 底部提示 */}
      <div className="mt-2 text-xs text-center text-gray-400">
        按 Enter 发送，Shift + Enter 换行
      </div>
    </div>
  );
}
